import styled from "@emotion/styled"
import { PButton, PText } from "@porsche-design-system/components-react"
import {
	colorContrastLow,
	colorContrastMedium,
	colorError,
	colorFrostedSoft,
	colorPrimary,
	fontWeightSemibold,
	leadingNormal,
	radiusXl,
	spacingStaticSm,
	spacingStaticXs,
	typescaleLg,
} from "@porsche-design-system/components-react/emotion"
import { newsMarkdownConfig } from "@slfinrtl/news-service-routes"
import Heading from "@tiptap/extension-heading"
import { Markdown } from "@tiptap/markdown"
import { EditorContent, useEditor, useEditorState } from "@tiptap/react"
import type { Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useEffect } from "react"
import type { ComponentProps } from "react"
import { useIntl } from "react-intl"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { FieldWrapper, Tooltip } from "src/components"
import { toSupportedContent } from "../newsContent"
import boldIcon from "./icons/bold.svg"
import headingIcon from "./icons/heading.svg"
import italicIcon from "./icons/italic.svg"
import orderedListIcon from "./icons/list-numbered.svg"
import { newsEditorMessages as messages } from "./newsEditor.messages"

const allowedNodes: ReadonlySet<string> = new Set(newsMarkdownConfig.allowedNodes)
const headingLevel = newsMarkdownConfig.allowedHeadingDepth
const extensionFor = (node: string) => (allowedNodes.has(node) ? undefined : false)

// keeps the given share of the color and fades the rest into the background
const lighten = (color: string, percent: number) => `color-mix(in srgb, ${color} ${percent}%, transparent)`

const extensions = [
	StarterKit.configure({
		blockquote: extensionFor("blockquote"),
		bold: extensionFor("strong"),
		bulletList: extensionFor("list"),
		code: extensionFor("inlineCode"),
		codeBlock: extensionFor("code"),
		document: extensionFor("root"),
		hardBreak: extensionFor("break"),
		heading: false,
		horizontalRule: extensionFor("thematicBreak"),
		italic: extensionFor("emphasis"),
		link: extensionFor("link"),
		listItem: extensionFor("listItem"),
		orderedList: extensionFor("list"),
		paragraph: extensionFor("paragraph"),
		strike: extensionFor("delete"),
		text: extensionFor("text"),
		trailingNode: false,
		underline: extensionFor("underline"),
	}),
	...(allowedNodes.has("heading")
		? [
				// Since 3.32.2 the default turns headings of unsupported levels into paragraphs and drops the "#".
				// Keeping the heading node lets toSupportedContent show them as plain text with their "#".
				Heading.extend({
					parseMarkdown: (token, helpers) =>
						helpers.createNode(
							"heading",
							{ level: token.depth ?? 1 },
							helpers.parseInline(token.tokens ?? []),
						),
				}).configure({ levels: [headingLevel] }),
			]
		: []),
	Markdown,
]

interface NewsContentEditorProps {
	description?: string
	/** makes the editor read only and shows the hint in a tooltip on hover */
	disabledHint?: string
	errorMessage?: string
	label: string
	onBlur?: () => void
	/** receives the content as markdown */
	onChange: (markdown: string) => void
	required?: boolean
	/** markdown */
	value: string
}

const setMarkdownContent = (editor: Editor, markdown: string) => {
	const content = toSupportedContent(editor.markdown?.parse(markdown) ?? { type: "doc" }, {
		headingLevel,
		marks: new Set(Object.keys(editor.schema.marks)),
		nodes: new Set(Object.keys(editor.schema.nodes)),
	})
	editor.commands.setContent(content, { emitUpdate: false })
}

export const NewsContentEditor = ({
	description,
	disabledHint,
	errorMessage,
	label,
	onBlur,
	onChange,
	required,
	value,
}: NewsContentEditorProps) => {
	const readOnly = !!disabledHint
	const editor = useEditor({
		editable: !readOnly,
		editorProps: {
			attributes: {
				"aria-disabled": `${readOnly}`,
				"aria-invalid": `${!!errorMessage}`,
				"aria-label": label,
				"aria-multiline": "true",
				"aria-required": `${!!required}`,
				role: "textbox",
			},
		},
		extensions,
		onBlur: () => onBlur?.(),
		onUpdate: ({ editor: updatedEditor }) => onChange(updatedEditor.getMarkdown()),
	})

	useEffect(() => {
		if (editor && editor.getMarkdown() !== value) {
			setMarkdownContent(editor, value)
		}
	}, [editor, value])

	return (
		<FieldWrapper
			disabled={readOnly}
			label={label}
			required={required}
			error={errorMessage ? { message: errorMessage } : undefined}
		>
			{description && (
				<Description $isDisabled={readOnly} aria-disabled={readOnly ? true : undefined}>
					<PText size="xs" color="contrast-medium">
						{description}
					</PText>
				</Description>
			)}
			<Tooltip content={disabledHint}>
				<EditorBox $hasError={!!errorMessage} $isDisabled={readOnly}>
					{editor && !readOnly && <Toolbar editor={editor} />}
					<EditorContent editor={editor} />
				</EditorBox>
			</Tooltip>
		</FieldWrapper>
	)
}

const Toolbar = ({ editor }: { editor: Editor }) => {
	const { formatMessage } = useIntl()
	const { isBold, isBulletList, isHeading, isItalic, isOrderedList } = useEditorState({
		editor,
		selector: ({ editor: currentEditor }) => ({
			isBold: currentEditor.isActive("bold"),
			isBulletList: currentEditor.isActive("bulletList"),
			isHeading: currentEditor.isActive("heading", { level: headingLevel }),
			isItalic: currentEditor.isActive("italic"),
			isOrderedList: currentEditor.isActive("orderedList"),
		}),
	})

	return (
		<div role="toolbar" aria-label={formatMessage(messages.toolbarLabel)}>
			<ToolbarButtons>
				<ToolbarButton
					isActive={isHeading}
					iconSource={headingIcon}
					label={formatMessage(messages.toolbarHeading)}
					onClick={() => editor.chain().focus().toggleHeading({ level: headingLevel }).run()}
				/>
				<ToolbarButton
					isActive={isBold}
					iconSource={boldIcon}
					label={formatMessage(messages.toolbarBold)}
					onClick={() => editor.chain().focus().toggleBold().run()}
				/>
				<ToolbarButton
					isActive={isItalic}
					iconSource={italicIcon}
					label={formatMessage(messages.toolbarItalic)}
					onClick={() => editor.chain().focus().toggleItalic().run()}
				/>
				<ToolbarButton
					icon="list"
					isActive={isBulletList}
					label={formatMessage(messages.toolbarBulletList)}
					onClick={() => editor.chain().focus().toggleBulletList().run()}
				/>
				<ToolbarButton
					isActive={isOrderedList}
					iconSource={orderedListIcon}
					label={formatMessage(messages.toolbarOrderedList)}
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
				/>
			</ToolbarButtons>
		</div>
	)
}

interface ToolbarButtonProps {
	icon?: ComponentProps<typeof PButton>["icon"]
	iconSource?: string
	isActive: boolean
	label: string
	onClick: () => void
}

const ToolbarButton = ({ icon = "none", iconSource, isActive, label, onClick }: ToolbarButtonProps) => (
	<PButton
		type="button"
		compact
		hideLabel
		icon={icon}
		iconSource={iconSource}
		variant={isActive ? "primary" : "secondary"}
		aria={{ "aria-pressed": isActive }}
		onClick={onClick}
	>
		{label}
	</PButton>
)

// pulls the description up to the label, like the description of the pds input fields
const Description = styled("div", { shouldForwardProp })<{ $isDisabled: boolean }>`
	margin-block-start: calc(-1 * ${spacingStaticXs});
	opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
`

const ToolbarButtons = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${spacingStaticXs};
	padding: ${spacingStaticSm};
	border-block-end: 1px solid ${colorContrastLow};
`

const EditorBox = styled("div", { shouldForwardProp })<{ $hasError: boolean; $isDisabled: boolean }>`
	border: 1px solid ${({ $hasError, $isDisabled }) => ($isDisabled ? lighten(colorContrastLow, 20) : $hasError ? colorError : colorContrastMedium)};
	border-radius: ${radiusXl};
	background-color: ${({ $isDisabled }) => ($isDisabled ? colorFrostedSoft : "transparent")};
	color: ${({ $isDisabled }) => ($isDisabled ? lighten(colorContrastMedium, 35) : "inherit")};
	cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "auto")};

	&:focus-within {
		border-color: ${({ $hasError }) => ($hasError ? colorError : colorPrimary)};
	}

	.ProseMirror {
		min-height: 10rem;
		padding: ${spacingStaticSm};
		outline: none;
		cursor: inherit;
	}

	.ProseMirror p {
		margin: 0;
	}

	.ProseMirror h2 {
		margin: 0;
		font-size: ${typescaleLg};
		font-weight: ${fontWeightSemibold};
		line-height: ${leadingNormal};
	}

	/* more specific than the resets of the margins above, otherwise they win */
	.ProseMirror > :is(p, h2, ul, ol) + :is(p, h2, ul, ol) {
		margin-top: ${spacingStaticSm};
	}

	.ProseMirror ul,
	.ProseMirror ol {
		margin: 0;
		padding-inline-start: 1.5rem;
	}
`
